// The Stores table's columns (WEB_PLAN.md §5.5): one spec per column with the cheap key a header sort uses and the
// cell the CSV export writes, so the table, its sort and the downloaded file always agree. Cells render in
// StoresTable.tsx; the words and colors come from the same domain/ and features/common rules as iOS RecordRow.
//
// Values iOS keeps local-only (visit count, last visit / result / activity, next scheduled) come from the derived
// stats (WEB_PLAN.md §3.10); "Last updated" is the row's `modified_at`, never the payload's stale `updatedAt`.

import { RECORD_TYPES, RECORD_TYPE_TITLES } from '@/contracts/enums';
import { customValuesToMap, fieldValueDate, fieldValueDisplay, type FieldValue } from '@/contracts/fieldValue';
import {
  addressOneLine,
  recordDisplayName,
  type CustomFieldPayload,
  type LeadSourcePayload,
  type RecordPayload,
  type TerritoryPayload,
} from '@/contracts/models';
import { idKey, isUuid } from '@/contracts/uuid';
import { parseWireDate } from '@/contracts/wireDate';
import type { DataItem } from '@/data/lists';
import { statsOf, type DerivedStats, type RecordStats } from '@/domain/derived';
import { formatMoney } from '@/domain/format';
import { stageFor } from '@/domain/pipelines';
import { compareLocalized, fullName, lookup } from '@/domain/util';
import { visitRecency, type StoreRowLookups } from '@/features/common/storeRowModel';
import { csvDate, csvDateTime, type CsvValue } from './csv';

// MARK: - Rows

/** One table row: the item (with its sync meta and newer-app flag), its payload and its derived stats. */
export interface StoreListRow {
  item: DataItem<RecordPayload>;
  /** `item.value`: the best-effort decode for an item written by a newer app (shown read-only). */
  record: RecordPayload;
  stats: RecordStats;
}

/** Rows for every item, in replica order. Build once per list or stats change. */
export function buildRows(items: readonly DataItem<RecordPayload>[], stats: DerivedStats | undefined): StoreListRow[] {
  return items.map((item) => ({ item, record: item.value, stats: statsOf(stats, item.id) }));
}

/** Everything a column needs besides the row. Build once per data change. */
export interface StoreColumnContext {
  /** Users, pipelines, visit results, stats, currency and the custom fields (`useStoreRowLookups()`). */
  lookups: StoreRowLookups;
  /** Territories by lower-case id. */
  territories: ReadonlyMap<string, TerritoryPayload>;
  /** Lead sources by lower-case id. */
  sources: ReadonlyMap<string, LeadSourcePayload>;
  /** Wall clock ("Overdue"); `lookups.now` only moves once a day. */
  now: Date;
}

// MARK: - Specs

export type SortKey = string | number | undefined;
export type ColumnGroup = 'store' | 'activity' | 'custom';

export const COLUMN_GROUP_TITLES: Readonly<Record<ColumnGroup, string>> = {
  store: 'Store',
  activity: 'Visits and activity',
  custom: 'Custom fields',
};

export interface StoreColumnSpec {
  /** Base ids are plain words; custom field columns are `cf:<lower-case field id>`. */
  id: string;
  title: string;
  group: ColumnGroup;
  /** Shown until the viewer chooses otherwise (saved per browser, columnPrefs.ts). */
  defaultVisible: boolean;
  /** Name always stays: a row needs something to read and click. */
  hideable: boolean;
  align: 'left' | 'right';
  /** Width in px (virtualized rows need fixed widths); `grow` shares the spare width. */
  size: number;
  grow?: number;
  /** The first header click sorts descending (amounts, counts, dates: biggest or newest first). */
  descFirst?: boolean;
  /** Cheap key for header sorting: numbers compare numerically, text by locale; undefined/'' sort last. */
  sortKey: (row: StoreListRow, ctx: StoreColumnContext) => SortKey;
  /** The CSV cell. Numbers stay numbers so a spreadsheet can sum them. */
  csv: (row: StoreListRow, ctx: StoreColumnContext) => CsvValue;
  /** Header in the CSV file when it says more than the table ("Value (USD)"). */
  csvTitle?: (ctx: StoreColumnContext) => string;
  /** The custom field behind a `cf:` column. */
  field?: CustomFieldPayload;
}

const time = (d: Date | undefined): number | undefined => d?.getTime();
const text = (s: string): string | undefined => (s === '' ? undefined : s);

function ownerName(r: RecordPayload, ctx: StoreColumnContext): string | undefined {
  const u = lookup(ctx.lookups.users, r.ownerId);
  return u ? fullName(u) || 'Unnamed' : undefined;
}

/**
 * Calendar days since the last visit (0 = today; a future-dated visit counts as today); undefined = never. The same
 * rule as the cell's `visitRecency`, so the number sorted and exported is the one on screen.
 */
export function daysSince(row: StoreListRow, now: Date): number | undefined {
  return visitRecency(row.stats.lastVisitAt, now).days;
}

export const BASE_COLUMNS: readonly StoreColumnSpec[] = [
  {
    id: 'name',
    title: 'Name',
    group: 'store',
    defaultVisible: true,
    hideable: false,
    align: 'left',
    size: 280,
    grow: 2,
    sortKey: (row) => recordDisplayName(row.record),
    csv: (row) => recordDisplayName(row.record),
  },
  {
    id: 'type',
    title: 'Type',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 120,
    sortKey: (row) => RECORD_TYPES.indexOf(row.record.type),
    csv: (row) => RECORD_TYPE_TITLES[row.record.type] ?? '',
  },
  {
    id: 'company',
    title: 'Company',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 180,
    sortKey: (row) => text(row.record.company),
    csv: (row) => row.record.company,
  },
  {
    id: 'address',
    title: 'Address',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 260,
    grow: 1,
    sortKey: (row) => text(addressOneLine(row.record.address)),
    csv: (row) => addressOneLine(row.record.address),
  },
  {
    id: 'phone',
    title: 'Phone',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 150,
    sortKey: (row) => text(row.record.phone),
    csv: (row) => row.record.phone,
  },
  {
    id: 'email',
    title: 'Email',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 200,
    sortKey: (row) => text(row.record.email),
    csv: (row) => row.record.email,
  },
  {
    id: 'stage',
    title: 'Stage',
    group: 'store',
    defaultVisible: true,
    hideable: true,
    align: 'left',
    size: 150,
    // Pipeline order, as the iOS "Stage" sort (stores without a stage last).
    sortKey: (row, ctx) => stageFor(row.record, ctx.lookups.pipelines)?.order,
    csv: (row, ctx) => stageFor(row.record, ctx.lookups.pipelines)?.name ?? '',
  },
  {
    id: 'priority',
    title: 'Priority',
    group: 'store',
    defaultVisible: true,
    hideable: true,
    align: 'left',
    size: 96,
    // The letters sort in heat order (A hottest … F coldest), so ascending reads A first.
    sortKey: (row) => row.record.priority,
    csv: (row) => row.record.priority,
  },
  {
    id: 'value',
    descFirst: true,
    title: 'Value',
    group: 'store',
    defaultVisible: true,
    hideable: true,
    align: 'right',
    size: 120,
    sortKey: (row) => (Number.isFinite(row.record.value) ? row.record.value : undefined),
    csv: (row) => (Number.isFinite(row.record.value) ? row.record.value : null),
    csvTitle: (ctx) => `Value (${ctx.lookups.currencyCode})`,
  },
  {
    id: 'owner',
    title: 'Owner',
    group: 'store',
    defaultVisible: true,
    hideable: true,
    align: 'left',
    size: 180,
    sortKey: (row, ctx) => ownerName(row.record, ctx),
    csv: (row, ctx) => ownerName(row.record, ctx) ?? 'Unassigned',
  },
  {
    id: 'territory',
    title: 'Territory',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 160,
    sortKey: (row, ctx) => lookup(ctx.territories, row.record.territoryId)?.name,
    csv: (row, ctx) => lookup(ctx.territories, row.record.territoryId)?.name ?? '',
  },
  {
    id: 'visits',
    descFirst: true,
    title: 'Visits',
    group: 'activity',
    defaultVisible: true,
    hideable: true,
    align: 'right',
    size: 90,
    sortKey: (row) => row.stats.visitCount,
    csv: (row) => row.stats.visitCount,
  },
  {
    id: 'lastVisit',
    descFirst: true,
    title: 'Last visit',
    group: 'activity',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 170,
    sortKey: (row) => time(row.stats.lastVisitAt),
    csv: (row) => csvDateTime(row.stats.lastVisitAt),
  },
  {
    id: 'daysSinceVisit',
    descFirst: true,
    title: 'Days since visit',
    group: 'activity',
    defaultVisible: true,
    hideable: true,
    align: 'left',
    size: 140,
    sortKey: (row, ctx) => daysSince(row, ctx.lookups.now),
    csv: (row, ctx) => daysSince(row, ctx.lookups.now) ?? 'Never',
  },
  {
    id: 'lastResult',
    title: 'Last result',
    group: 'activity',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 160,
    sortKey: (row, ctx) => lookup(ctx.lookups.visitResults, row.stats.lastVisitResultId)?.name,
    csv: (row, ctx) => lookup(ctx.lookups.visitResults, row.stats.lastVisitResultId)?.name ?? '',
  },
  {
    id: 'lastActivity',
    descFirst: true,
    title: 'Last activity',
    group: 'activity',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 170,
    sortKey: (row) => time(row.stats.lastActivityAt),
    csv: (row) => csvDateTime(row.stats.lastActivityAt),
  },
  {
    id: 'nextScheduled',
    title: 'Next scheduled',
    group: 'activity',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 170,
    sortKey: (row) => time(row.stats.nextScheduledAt),
    csv: (row) => csvDateTime(row.stats.nextScheduledAt),
  },
  {
    id: 'tags',
    title: 'Tags',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 180,
    sortKey: (row) => text(row.record.tags.join(', ')),
    csv: (row) => row.record.tags.join(', '),
  },
  {
    id: 'source',
    title: 'Source',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 150,
    sortKey: (row, ctx) => lookup(ctx.sources, row.record.sourceId)?.name,
    csv: (row, ctx) => lookup(ctx.sources, row.record.sourceId)?.name ?? '',
  },
  {
    id: 'created',
    descFirst: true,
    title: 'Created',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 170,
    sortKey: (row) => time(parseWireDate(row.record.createdAt)),
    csv: (row) => csvDateTime(parseWireDate(row.record.createdAt)),
  },
  {
    id: 'updated',
    descFirst: true,
    title: 'Last updated',
    group: 'store',
    defaultVisible: false,
    hideable: true,
    align: 'left',
    size: 170,
    sortKey: (row) => time(parseWireDate(row.item.modifiedAt)),
    csv: (row) => csvDateTime(parseWireDate(row.item.modifiedAt)),
  },
];

export const BASE_COLUMN_IDS: ReadonlySet<string> = new Set(BASE_COLUMNS.map((c) => c.id));

// MARK: - Custom field columns

const CUSTOM_PREFIX = 'cf:';

export function customColumnId(fieldId: string): string {
  return `${CUSTOM_PREFIX}${idKey(fieldId)}`;
}

/** A known base id or `cf:<uuid>` (what may be saved in column prefs or the URL). */
export function isColumnId(id: unknown): id is string {
  if (typeof id !== 'string') return false;
  if (BASE_COLUMN_IDS.has(id)) return true;
  return id.startsWith(CUSTOM_PREFIX) && isUuid(id.slice(CUSTOM_PREFIX.length));
}

// Parsing customValues is not free; the table and the sort read several custom columns per row, so keep one map per
// payload object (payloads are immutable: a changed record is a new object).
const valueCache = new WeakMap<RecordPayload, Map<string, FieldValue>>();

/** The record's value for a custom field (upper-case keys, as `cardFieldsLine` reads them). */
export function customValueOf(record: RecordPayload, fieldId: string): FieldValue | undefined {
  let map = valueCache.get(record);
  if (!map) {
    map = customValuesToMap(record.customValues);
    valueCache.set(record, map);
  }
  return map.get(fieldId.toUpperCase());
}

/** The text a custom field shows: money for currency fields, else iOS `FieldValue.displayString`. */
export function customFieldText(field: CustomFieldPayload, value: FieldValue | undefined, currencyCode: string, locale?: string): string {
  if (!value) return '';
  if (field.type === 'currency' && value.kind === 'number') return formatMoney(value.value, currencyCode, locale);
  return fieldValueDisplay(value, locale);
}

function customSortKey(value: FieldValue | undefined): SortKey {
  if (!value) return undefined;
  switch (value.kind) {
    case 'number':
      return Number.isFinite(value.value) ? value.value : undefined;
    case 'bool':
      return value.value ? 1 : 0;
    case 'date':
      return fieldValueDate(value)?.getTime();
    case 'text':
      return text(value.value);
    case 'options':
      return text(value.value.join(', '));
  }
}

function customCsv(field: CustomFieldPayload, value: FieldValue | undefined, ctx: StoreColumnContext): CsvValue {
  if (!value) return '';
  switch (value.kind) {
    case 'number':
      return Number.isFinite(value.value) ? value.value : '';
    case 'date':
      return csvDate(fieldValueDate(value)) || value.value;
    default:
      return customFieldText(field, value, ctx.lookups.currencyCode, ctx.lookups.locale);
  }
}

/**
 * One column per custom field flagged "Show on card" (the fields the company chose to see on every store), in field
 * order. A value only counts when the field applies to the store's type, as on the card.
 */
export function customColumns(fields: readonly CustomFieldPayload[]): StoreColumnSpec[] {
  return fields
    .filter((f) => f.showOnCard)
    .sort((a, b) => a.order - b.order)
    .map((field): StoreColumnSpec => {
      const valueOf = (row: StoreListRow) =>
        field.appliesTo.includes(row.record.type) ? customValueOf(row.record, field.id) : undefined;
      const numeric = field.type === 'number' || field.type === 'currency';
      const title = field.name.trim() === '' ? 'Untitled field' : field.name;
      return {
        id: customColumnId(field.id),
        title,
        group: 'custom',
        defaultVisible: false,
        hideable: true,
        align: numeric ? 'right' : 'left',
        descFirst: numeric || field.type === 'date',
        size: field.type === 'multilineText' ? 220 : 160,
        sortKey: (row) => customSortKey(valueOf(row)),
        csv: (row, ctx) => customCsv(field, valueOf(row), ctx),
        // The table's header, so the file and the screen name the column alike.
        csvTitle: (ctx) => (field.type === 'currency' ? `${title} (${ctx.lookups.currencyCode})` : title),
        field,
      };
    });
}

/** Base columns, then the custom field columns. */
export function allColumns(customFields: readonly CustomFieldPayload[]): StoreColumnSpec[] {
  return [...BASE_COLUMNS, ...customColumns(customFields)];
}

// MARK: - Header sorting

export interface ColumnSort {
  id: string;
  desc: boolean;
}

function compareKeys(a: SortKey, b: SortKey): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return compareLocalized(String(a), String(b));
}

const isEmptyKey = (k: SortKey) => k === undefined || k === '' || (typeof k === 'number' && Number.isNaN(k));

/**
 * Rows sorted by one or more columns (Shift+click adds levels). Each key is computed once per row, not per
 * comparison. Empty values sort last in both directions; ties fall back to the name, then the input order.
 */
export function sortRowsByColumns(
  rows: readonly StoreListRow[],
  sorts: readonly ColumnSort[],
  specs: ReadonlyMap<string, StoreColumnSpec>,
  ctx: StoreColumnContext,
): StoreListRow[] {
  const levels = sorts
    .map((s) => ({ spec: specs.get(s.id), desc: s.desc }))
    .filter((l): l is { spec: StoreColumnSpec; desc: boolean } => l.spec !== undefined);
  if (levels.length === 0) return [...rows];
  const keys = levels.map((l) => rows.map((r) => l.spec.sortKey(r, ctx)));
  const names = rows.map((r) => recordDisplayName(r.record));
  const order = rows.map((_, i) => i);
  order.sort((i, j) => {
    for (let l = 0; l < levels.length; l += 1) {
      const a = keys[l]![i];
      const b = keys[l]![j];
      const ea = isEmptyKey(a);
      const eb = isEmptyKey(b);
      if (ea || eb) {
        if (ea && eb) continue;
        return ea ? 1 : -1;
      }
      const c = compareKeys(a, b);
      if (c !== 0) return levels[l]!.desc ? -c : c;
    }
    return compareLocalized(names[i]!, names[j]!) || i - j;
  });
  return order.map((i) => rows[i]!);
}

// MARK: - CSV

/** The export: the given columns (as shown, in order) for the given rows (as sorted). */
export function exportTable(
  rows: readonly StoreListRow[],
  columns: readonly StoreColumnSpec[],
  ctx: StoreColumnContext,
): { header: string[]; rows: CsvValue[][] } {
  return {
    header: columns.map((c) => (c.csvTitle ? c.csvTitle(ctx) : c.title)),
    rows: rows.map((r) => columns.map((c) => c.csv(r, ctx))),
  };
}
